import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Map, Mail, Eye, EyeOff, Save, Loader2, Shield, Sparkles, KeyRound, Copy, Trash2 } from "lucide-react";

// The API never returns stored credentials — only `<field>_configured` booleans.
// These inputs are therefore write-only: they start empty and are sent only when
// the user types a new value, so saving the form never wipes a stored key.
function SecretInput({ value, onChange, placeholder, testId }) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <Input
        type={visible ? "text" : "password"}
        value={value || ""}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="bg-secondary border-transparent pr-10 font-mono text-sm"
        data-testid={testId}
      />
      <button
        type="button"
        onClick={() => setVisible(!visible)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
      >
        {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
}

function ConfiguredBadge({ configured }) {
  return configured
    ? <Badge variant="outline" className="text-[10px] text-emerald-600">Configurado</Badge>
    : <Badge variant="outline" className="text-[10px] text-muted-foreground">No configurado</Badge>;
}

export function APIKeysTab({ restaurant, authAxios, onRefresh }) {
  const [saving, setSaving] = useState(false);
  const [mapsKey, setMapsKey] = useState("");
  const [resendKey, setResendKey] = useState("");
  const [senderEmail, setSenderEmail] = useState(restaurant?.sender_email || "");
  const [currency, setCurrency] = useState(restaurant?.currency || "EUR");
  const [taxRate, setTaxRate] = useState(restaurant?.tax_rate ?? "");
  const [zasStatus, setZasStatus] = useState(null);
  const [newZasKey, setNewZasKey] = useState("");
  const [zasBusy, setZasBusy] = useState(false);

  const mapsConfigured = !!restaurant?.google_maps_key_configured;
  const resendConfigured = !!restaurant?.resend_api_key_configured;
  const aiConfigured = !!restaurant?.ai_api_key_configured;

  const loadZasStatus = async () => {
    try { const { data } = await authAxios.get("/v1/zas/credentials"); setZasStatus(data); }
    catch { setZasStatus({ configured: false, active: false }); }
  };

  useEffect(() => { loadZasStatus(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const generateZasKey = async () => {
    setZasBusy(true);
    try { const { data } = await authAxios.post("/v1/zas/credentials"); setNewZasKey(data.apiKey); await loadZasStatus(); toast.success("Clave ZAS generada"); }
    catch { toast.error("No se pudo generar la clave"); }
    setZasBusy(false);
  };
  const revokeZasKey = async () => {
    if (!window.confirm("¿Revocar la clave ZAS de esta unidad?")) return;
    setZasBusy(true);
    try { await authAxios.delete("/v1/zas/credentials"); setNewZasKey(""); await loadZasStatus(); toast.success("Clave ZAS revocada"); }
    catch { toast.error("No se pudo revocar la clave"); }
    setZasBusy(false);
  };

  useEffect(() => {
    // Secret inputs stay empty on reload — the stored value is never sent back.
    setMapsKey("");
    setResendKey("");
    setSenderEmail(restaurant?.sender_email || "");
    setCurrency(restaurant?.currency || "EUR");
    setTaxRate(restaurant?.tax_rate ?? "");
  }, [restaurant]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        sender_email: senderEmail,
        currency,
        tax_rate: taxRate ? parseFloat(taxRate) : 0,
      };
      // Only send a credential the user actually typed, so an untouched field
      // is never overwritten with an empty string.
      if (mapsKey.trim()) payload.google_maps_key = mapsKey.trim();
      if (resendKey.trim()) payload.resend_api_key = resendKey.trim();

      await authAxios.patch("/restaurant", payload);
      setMapsKey("");
      setResendKey("");
      toast.success("Claves guardadas!");
      onRefresh?.();
    } catch { toast.error("Error al guardar"); }
    setSaving(false);
  };

  return (
    <div className="space-y-6">
      <Card className="bg-card border-border/50">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-emerald-500" />
            <CardTitle className="uppercase">Claves API</CardTitle>
          </div>
          <CardDescription>
            Cada restaurante usa sus propias claves. Tus claves nunca son compartidas con otros
            restaurantes y nunca se muestran de nuevo despues de guardarlas.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-3 rounded-xl border border-orange-500/25 bg-orange-500/5 p-4" data-testid="zas-api-key-card">
            <div className="flex items-center gap-2">
              <KeyRound className="w-4 h-4 text-orange-500" />
              <Label className="font-semibold">Conector ZAS</Label>
              <ConfiguredBadge configured={!!zasStatus?.active} />
            </div>
            <p className="text-xs text-muted-foreground">
              Esta clave pertenece exclusivamente a esta unidad. ZAS no puede usarla para otro restaurant_id.
            </p>
            {newZasKey && <div className="space-y-2">
              <div className="flex gap-2"><Input readOnly value={newZasKey} className="font-mono text-xs" data-testid="zas-api-key-once" /><Button variant="outline" onClick={() => navigator.clipboard.writeText(newZasKey)}><Copy className="w-4 h-4" /></Button></div>
              <p className="text-xs font-semibold text-orange-600">Copia ahora: por seguridad, la clave no se mostrará otra vez.</p>
            </div>}
            <div className="flex flex-wrap gap-2">
              <Button onClick={generateZasKey} disabled={zasBusy} data-testid="zas-api-key-generate">
                {zasBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                {zasStatus?.active ? "Rotar clave" : "Generar clave"}
              </Button>
              {zasStatus?.active && <Button variant="destructive" onClick={revokeZasKey} disabled={zasBusy} data-testid="zas-api-key-revoke"><Trash2 className="w-4 h-4" />Revocar</Button>}
            </div>
          </div>

          {/* Google Maps */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Map className="w-4 h-4 text-blue-500" />
              <Label className="font-semibold">Google Maps API Key</Label>
              <ConfiguredBadge configured={mapsConfigured} />
            </div>
            <SecretInput
              value={mapsKey}
              onChange={setMapsKey}
              placeholder={mapsConfigured ? "Escribe una clave nueva para reemplazarla" : "AIzaSy..."}
              testId="api-maps-key"
            />
            <p className="text-xs text-muted-foreground">
              Necesario para autocompletado de direcciones y mapa de entregas.
              <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noreferrer" className="text-blue-500 ml-1">Obtener clave</a>
            </p>
          </div>

          {/* Resend Email */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-purple-500" />
              <Label className="font-semibold">Resend API Key (Email)</Label>
              <ConfiguredBadge configured={resendConfigured} />
            </div>
            <SecretInput
              value={resendKey}
              onChange={setResendKey}
              placeholder={resendConfigured ? "Escribe una clave nueva para reemplazarla" : "re_..."}
              testId="api-resend-key"
            />
            <p className="text-xs text-muted-foreground">
              Para enviar emails de confirmacion, reservas y notificaciones.
              <a href="https://resend.com/api-keys" target="_blank" rel="noreferrer" className="text-blue-500 ml-1">Obtener clave</a>
            </p>
          </div>

          {/* Sender Email */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-gray-500" />
              <Label className="font-semibold">Email de envio</Label>
            </div>
            <Input
              value={senderEmail}
              onChange={e => setSenderEmail(e.target.value)}
              placeholder="pedidos@tu-restaurante.com"
              className="bg-secondary border-transparent"
              data-testid="api-sender-email"
            />
            <p className="text-xs text-muted-foreground">El email que aparecera como remitente en las notificaciones.</p>
          </div>

          {/* AI provider key — read-only status. Managed by the Central de IA. */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-500" />
              <Label className="font-semibold">Clave API de Inteligencia Artificial</Label>
              <ConfiguredBadge configured={aiConfigured} />
            </div>
            <p className="text-xs text-muted-foreground" data-testid="api-ai-key-status">
              La gestion de las claves de IA pasa a la Central de IA, que las guarda cifradas.
              Desde aqui solo se consulta el estado; la clave no se muestra ni se edita.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Fiscal Config */}
      <Card className="bg-card border-border/50">
        <CardHeader>
          <CardTitle className="uppercase">Configuracion Fiscal</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="font-semibold">Moneda</Label>
              <select
                value={currency}
                onChange={e => setCurrency(e.target.value)}
                className="w-full h-10 rounded-md bg-secondary border-transparent px-3 text-sm"
                data-testid="api-currency"
              >
                <option value="EUR">EUR - Euro</option>
                <option value="BRL">BRL - Real</option>
                <option value="USD">USD - Dolar</option>
                <option value="GBP">GBP - Libra</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label className="font-semibold">IVA / Impuesto (%)</Label>
              <Input
                type="number"
                value={taxRate}
                onChange={e => setTaxRate(e.target.value)}
                placeholder="21"
                className="bg-secondary border-transparent"
                data-testid="api-tax-rate"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Button onClick={handleSave} disabled={saving} className="gap-2 glow-orange" data-testid="api-save-btn">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        Guardar Claves
      </Button>
    </div>
  );
}

